import type { ConsultOrderPhase } from "../hooks/useConsultOrderLifecycle";

// ITAM-2622 (3.6a) — pure: no React, no store, no I/O. Type-only import, so
// there is no runtime dependency back on the hook.
//
// ONE rule, two consumers: `onConsultInactive` (should consult end open the
// 15-minute grace window, or just reset?) and the logout gate (must logout
// confirm before it discards?). Sharing the rule is what stops those two
// answers from drifting apart.
//
// THE RULE THIS ENCODES: classify by the FACT of the draft, never by what else
// is covering the phase. A covering mechanism — an open modal, an active-consult
// block, a grace flag — has a different lifetime from the draft it is assumed
// to cover, and every earlier revision that appealed to one got the answer
// wrong. Every `false` below is backed by a source fact about the draft itself.
//
// There is deliberately NO `default` branch: adding a phase to
// `ConsultOrderPhase` must be a TypeScript error here, not a silent
// misclassification.

export function isConsultDraftUnfinished(
  phase: ConsultOrderPhase,
  /** The context prefill is hydrated — i.e. a form was entered for this consult. */
  hasStartedDraft: boolean,
  /** `rfzoResult?.orderable === true`. */
  rfzoOrderable: boolean
): boolean {
  switch (phase) {
    // Live drafts. Entering either form hydrates the context, so the
    // unconditional `true` never actually differs from `hasStartedDraft` today;
    // it is stated unconditionally so a future path that skips hydration fails
    // safe.
    case "FORM":
    case "SUBMITTING":
    case "RFZO_FORM":
    case "RFZO_SUBMITTING":
      return true;

    // An orderable therapy still needs its order. And a TERMINAL result no
    // longer means the session is over: with the consult still live,
    // `closeRfzoResult` returns to CHIP precisely so that "after a due-later /
    // no-content outcome a regular order stays possible" — so a CnC draft can
    // be alive underneath a terminal result.
    case "RFZO_RESULT":
      return rfzoOrderable || hasStartedDraft;

    // IDLE cannot hold a draft today (the only `setPhase("IDLE")` runs one line
    // after `clearDraft()`, and nothing persists a draft to storage) — written
    // as `hasStartedDraft` rather than `false` so the answer follows the fact
    // rather than the invariant.
    //
    // ARMING outlives `activeConsult` by up to two minutes and renders nothing
    // over the logout control, so the window is genuinely clickable. The draft
    // sitting in it belongs to an already-ended consult and is already
    // condemned, which makes this an accepted rare FAIL-SAFE warning rather
    // than a rescue: answering `false` would rest on a backend discovery
    // invariant holding forever.
    //
    // PROMPT/CHIP: a form was entered and closed back to the chip; the draft
    // survives.
    case "IDLE":
    case "ARMING":
    case "CHIP":
    case "PROMPT":
      return hasStartedDraft;

    // Submitted — and the flow forecloses the rest: `closeSuccess` runs
    // `resetToIdle` and the consult is marked ordered, so a leftover context
    // draft is abandoned by design rather than unfinished.
    case "SUCCESS":
      return false;
  }
}
